"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Helpers = void 0;
const i18n_js_1 = __importDefault(require("i18n-js"));
// @ts-ignore
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const weekdayFormatOptions = {
    weekday: 'long',
};
const weekdayDateAndMonthOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
};
const isMobile = {
    Android() {
        return navigator.userAgent.match(/Android/i);
    },
    iOS() {
        return (navigator.userAgent.match(/iPhone|iPad|iPod/i) ||
            // some apple tablets have user agents meant for macs, so we check if it's a mackintosh device AND has touch events
            (navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2));
    },
};
let flattenedList = [];
const flattenCollection = (collection, firstIteration = false) => {
    if (firstIteration) {
        flattenedList = [];
    }
    const stack = [...collection];
    while (stack.length) {
        const node = stack.shift();
        const nodeKey = node && Object.keys(node)[0];
        const nodeContent = node[nodeKey];
        const nodeType = nodeContent && nodeContent.type;
        flattenedList.push(node);
        if (nodeType === 'folder' && nodeContent.children) {
            flattenCollection(nodeContent.children, false);
        }
    }
    return flattenedList;
};
function convertFormatToMomentString(format) {
    let formatString = '';
    if (format.weekday) {
        formatString += 'dddd ';
    }
    if (format.year) {
        formatString += 'YYYY ';
    }
    if (format.month) {
        formatString += 'MMMM ';
    }
    if (format.day) {
        formatString += 'DD ';
    }
    if (format.hour) {
        formatString += 'HH ';
    }
    if (format.minute) {
        formatString += 'mm ';
    }
    return formatString.trim();
}
function getDateForTimezoneFormatting(value, timezone) {
    const date = new Date(value);
    if (process.env.PLATFORM === 'ds' && timezone) {
        // Format the bundled zone's wall time as UTC so Intl cannot apply stale browser timezone rules.
        const offset = moment_timezone_1.default.tz(date, timezone).utcOffset();
        return { date: new Date(date.getTime() + offset * 60000), timeZone: 'UTC' };
    }
    return { date, timeZone: timezone };
}
const Helpers = {
    getDaysBetweenDates(startDate, endDate) {
        const differenceInTime = endDate.getTime() - startDate.getTime();
        return differenceInTime / (1000 * 3600 * 24);
    },
    convertDateToTimezone(date, timezone, format) {
        const chromeVersion = this.getChromeVersion();
        const locale = i18n_js_1.default.currentLocale() === 'derobinson' ? 'de' : i18n_js_1.default.currentLocale();
        const isoDate = new Date(date).toISOString();
        // Use Moment.js for Chrome versions below 113
        if (chromeVersion !== null && chromeVersion < 113) {
            moment_timezone_1.default.locale(locale);
            const momentFormat = typeof format === 'string' ? format : convertFormatToMomentString(format);
            const momentDate = timezone ? moment_timezone_1.default.tz(isoDate, timezone) : (0, moment_timezone_1.default)(isoDate);
            return momentDate.format(momentFormat);
        }
        // Use Date constructor with time zone
        const { date: formattingDate, timeZone } = getDateForTimezoneFormatting(isoDate, timezone);
        const options = process.env.PLATFORM === 'ds' ? Object.assign(Object.assign({}, format), { timeZone }) : Object.assign({ timeZone }, format);
        let adjustedDate = formattingDate.toLocaleString(locale, options);
        // Adjust date format for German locale
        if (locale === 'de') {
            adjustedDate = adjustedDate.replace(/(\w+)\s(\d+)\.\s(\w+)/, '$1, $2. $3');
        }
        return adjustedDate;
    },
    isMobile,
    // make sure this is the "display" setting in the manifest.json
    isPWA() {
        return window.matchMedia('(display-mode: standalone)').matches;
    },
    convertHourToTimezone(date, timezone) {
        const { date: formattingDate, timeZone } = getDateForTimezoneFormatting(date, timezone);
        return formattingDate.toLocaleTimeString(i18n_js_1.default.currentLocale() === 'derobinson' ? 'de' : i18n_js_1.default.currentLocale(), {
            timeZone,
            hour: 'numeric',
            minute: i18n_js_1.default.currentLocale() === 'en' ? undefined : '2-digit',
            hour12: i18n_js_1.default.currentLocale() === 'en',
        });
    },
    getHourFromDate(date, timezone = undefined, locale = i18n_js_1.default.currentLocale(), hourOptions = { hour: '2-digit', minute: '2-digit' }) {
        const chromeVersion = this.getChromeVersion();
        const modifiedLocale = locale === 'derobinson' ? 'de' : locale;
        // Use Moment.js for Chrome versions below 113
        if (chromeVersion && chromeVersion < 113) {
            moment_timezone_1.default.locale(modifiedLocale);
            if (timezone) {
                return moment_timezone_1.default.tz(date, timezone).format(modifiedLocale === 'en' ? 'h:mm A' : 'HH:mm');
            }
            return (0, moment_timezone_1.default)(date).format(modifiedLocale === 'en' ? 'h:mm A' : 'HH:mm');
        }
        const { date: formattingDate, timeZone } = getDateForTimezoneFormatting(date, timezone);
        const options = Object.assign(Object.assign({}, hourOptions), { timeZone, hour12: modifiedLocale === 'en' });
        return formattingDate.toLocaleTimeString(modifiedLocale, options);
    },
    getWeekdayFromDate(date, timezone, format = weekdayFormatOptions) {
        return this.convertDateToTimezone(date, timezone, format);
    },
    getWeekdayDateAndMonth(dateString, timezone, formatOptions = weekdayDateAndMonthOptions) {
        const date = this.convertDateToTimezone(dateString, timezone, formatOptions);
        const today = this.convertDateToTimezone(new Date().toString(), timezone, formatOptions);
        let tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow = this.convertDateToTimezone(tomorrow.toString(), timezone, formatOptions);
        const isToday = date === today;
        const isTomorrow = date === tomorrow;
        let weekDay = this.convertDateToTimezone(dateString, timezone, {
            weekday: formatOptions.weekday,
        });
        const dayAndMonth = this.convertDateToTimezone(dateString, timezone, {
            day: formatOptions.day,
            month: formatOptions.month,
        });
        if (isToday) {
            weekDay = i18n_js_1.default.t('dictionary.dates.today');
        }
        if (isTomorrow) {
            weekDay = i18n_js_1.default.t('dictionary.dates.tomorrow');
        }
        const formattedDate = `${weekDay}, ${dayAndMonth}`;
        return formattedDate;
    },
    chunk(arr, size) {
        return Array.from({ length: Math.ceil(arr.length / size) }, (v, i) => arr.slice(i * size, i * size + size));
    },
    getRandomIndexFromCollection(collection) {
        return Math.floor(Math.random() * (collection.length - 1));
    },
    getNumericalDayOfTheWeek(day) {
        return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(day);
    },
    getNumericalMonthOfTheYear(month) {
        return [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ].indexOf(month);
    },
    compose(...fns) {
        return fns.reduce((f, g) => (...args) => f(g(...args)));
    },
    removeDuplicatesFromArray(array, property) {
        return array.reduce((acc, current) => {
            const isDuplicate = acc.find(item => item[property] === current[property]);
            if (isDuplicate) {
                return acc;
            }
            return acc.concat([current]);
        }, []);
    },
    capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    },
    isInViewport(element) {
        const bounding = element.getBoundingClientRect();
        return (bounding.top >= -element.offsetHeight &&
            bounding.left >= -element.offsetWidth &&
            bounding.right <= window.innerWidth + element.offsetWidth &&
            bounding.bottom <= window.innerHeight + element.offsetHeight);
    },
    convertHexToRGBA(hex, opacity) {
        const trimedHex = hex.replace('#', '');
        const r = parseInt(trimedHex.substring(0, 2), 16);
        const g = parseInt(trimedHex.substring(2, 4), 16);
        const b = parseInt(trimedHex.substring(4, 6), 16);
        return `rgba(${r},${g},${b},${opacity / 100})`;
    },
    isHexColorDark(hex) {
        const hexToRgb = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (hexToRgb) {
            const r = parseInt(hexToRgb[1], 16);
            const g = parseInt(hexToRgb[2], 16);
            const b = parseInt(hexToRgb[3], 16);
            const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
            return hsp < 150;
        }
        return null;
    },
    getTimeFromISODate(isoDate) {
        return isoDate
            .split('T')[1]
            .split('+')[0]
            .substr(0, 5);
    },
    getISOString(dateAndTime) {
        const date = dateAndTime.split(' ')[0];
        const time = dateAndTime.split(' ')[1];
        const dateInCorrectFormat = date.split('/').join('-');
        return `${dateInCorrectFormat}T${time}:00`;
    },
    // convert from YYYY-MM-DD to MMM DD, YYYY; ex. 2022-11-17 to Nov 17, 2022
    getDateFromString(date) {
        return new Date(date).toLocaleDateString('en-us', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    // convert date to a custom format
    // Ex. for EN - Fri, Aug 18, 2023 12hrs + AM/PM
    // Ex. for DE - Fri., 18. Aug. 2023 24hrs
    getDateFromStringDependingLocale(dateTime, locale, timezone = undefined, noYear = false, dayWeekMonthFormat = {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
    }) {
        const chromeVersion = Helpers.getChromeVersion();
        const modifiedLocale = locale === 'derobinson' ? 'de' : locale;
        // Use Moment.js for Chrome versions below 113
        if (chromeVersion && chromeVersion < 113) {
            moment_timezone_1.default.locale(modifiedLocale);
            let formatString;
            if (modifiedLocale === 'de') {
                formatString = noYear ? 'dddd, D. MMMM' : 'dddd, D. MMMM YYYY';
            }
            else {
                formatString = noYear ? 'dddd, MMMM D' : 'dddd, MMMM D, YYYY';
            }
            const date = timezone
                ? moment_timezone_1.default.tz(dateTime, timezone).format(formatString)
                : (0, moment_timezone_1.default)(dateTime).format(formatString);
            let timeFormat;
            if (locale === 'en') {
                timeFormat = 'h:mm A';
            }
            else {
                timeFormat = 'HH:mm';
            }
            const time = timezone ? moment_timezone_1.default.tz(dateTime, timezone).format(timeFormat) : (0, moment_timezone_1.default)(dateTime).format(timeFormat);
            return { date, time };
        }
        const { date, timeZone } = getDateForTimezoneFormatting(dateTime, timezone);
        // Set options for date and time formatting
        const dateOptions = Object.assign(Object.assign({}, dayWeekMonthFormat), { year: noYear ? undefined : 'numeric', timeZone });
        const timeOptions = {
            hour: '2-digit',
            minute: '2-digit',
            hour12: locale === 'en',
            timeZone,
        };
        // Format the date and time separately
        const dateFormatter = new Intl.DateTimeFormat(modifiedLocale, dateOptions);
        const timeFormatter = new Intl.DateTimeFormat(modifiedLocale, timeOptions);
        // Extract formatted date components
        const formattedDate = dateFormatter.format(date);
        const formattedTime = timeFormatter.format(date);
        return {
            date: formattedDate,
            time: formattedTime,
        };
    },
    prepareBookingFields(fields, type) {
        const params = fields
            .filter(field => field.type !== 'shopping_cart' && field.type !== 'section_heading')
            .reduce((params, currentField) => {
            if (currentField.type === 'multi_select_inline' || currentField.type === 'multi_select') {
                const multiSelectOptions = currentField.options.filter(option => option.selected);
                return `${params}${multiSelectOptions.reduce((previousOption, currentOption) => `${previousOption}&${type}${type !== 'stay' ? '[fields][' : '['}${currentField.id}][]=${currentOption.id}`, '')}`;
            }
            if (currentField.type === 'area_multi_select') {
                const multiSelectOptions = currentField.options.filter(option => option.selected);
                const multiSelectParams = multiSelectOptions.reduce((previousOption, currentOption) => {
                    const optionIdArray = Array(currentOption.quantity || 1).fill(currentOption.id);
                    return `${previousOption}${optionIdArray.reduce((acc, optionId) => `${acc}&${type}${type !== 'stay' ? '[fields][' : '['}${currentField.id}][]=${optionId}`, '')}`;
                }, '');
                return `${params}${multiSelectParams}`;
            }
            if (currentField.type === 'single_select' ||
                currentField.type === 'single_select_inline' ||
                currentField.type === 'area_select' ||
                currentField.type === 'table_select') {
                const selectedOption = currentField.value
                    ? currentField.options.find(option => option.label === currentField.value)
                    : currentField.options.find(option => option.selected);
                return `${params}&${type}${type !== 'stay' ? '[fields][' : '['}${currentField.id}]=${selectedOption.id}`;
            }
            return `${params}&${type}${type !== 'stay' ? '[fields][' : '['}${currentField.id}]=${currentField.value ? currentField.value : ''}`;
        }, '');
        return params;
    },
    throttle(action, throttleTimeOut) {
        let throttled = false;
        return (...args) => {
            if (!throttled) {
                throttled = true;
                action(...args);
                setTimeout(() => {
                    throttled = false;
                }, throttleTimeOut);
            }
        };
    },
    debounce(action, delay) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                action.apply(this, args);
            }, delay);
        };
    },
    lightenColor(color, percent) {
        const num = parseInt(color.replace('#', ''), 16);
        const amt = Math.round(2.55 * percent);
        // eslint-disable-next-line no-bitwise
        const R = (num >> 16) + amt;
        // eslint-disable-next-line no-bitwise
        const B = ((num >> 8) & 0x00ff) + amt;
        // eslint-disable-next-line no-bitwise
        const G = (num & 0x0000ff) + amt;
        // eslint-disable-next-line no-nested-ternary
        return `#${(0x1000000 +
            (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
            (B < 255 ? (B < 1 ? 0 : B) : 255) * 0x100 +
            (G < 255 ? (G < 1 ? 0 : G) : 255))
            .toString(16)
            .slice(1)}`;
    },
    loadThirdPartyScript(callback, scriptId, src, ucid, type, language) {
        const existingScript = document.getElementById(scriptId);
        if (!existingScript) {
            const script = document.createElement('script');
            script.src = src;
            script.id = scriptId;
            if (ucid) {
                script.setAttribute('ucid', ucid);
            }
            if (type) {
                script.setAttribute('type', type);
            }
            if (language) {
                script.setAttribute('language', language);
            }
            // $FlowIgnore: suppressing this error
            document.head.appendChild(script);
            script.onload = () => {
                callback();
            };
        }
        else {
            callback();
        }
    },
    loadUsercentricsScript(callback, ucid, language = 'en') {
        this.loadThirdPartyScript(callback, 'cookieboxUC', 'https://static.liebherr.com/cmp/master/cmp.js', ucid, 'application/javascript', language);
    },
    openModal(modalType, modalProps) {
        const event = new CustomEvent('openModal', {
            detail: {
                modalType,
                modalProps,
            },
        });
        window.dispatchEvent(event);
    },
    openSearchModal(modalType, modalProps) {
        const event = new CustomEvent('openSearchModal', {
            detail: {
                modalType,
                modalProps,
            },
        });
        window.dispatchEvent(event);
    },
    closeSearchModal() {
        const event = new CustomEvent('closeSearchModal', {});
        window.dispatchEvent(event);
    },
    flattenCollection,
    loadManifestByApp(slug) {
        const brandKey = sessionStorage.getItem('brandKey') ? sessionStorage.getItem('brandKey') : '';
        // eslint-disable-next-line  no-restricted-globals
        const { origin } = location;
        // $FlowIgnore: suppressing this error
        const pathToManifest = window.location.hostname !== 'localhost'
            ? `${origin}/manifest/${slug}.json?api_key=${brandKey}`
            : '/manifest.json';
        const element = document.createElement('link');
        element.setAttribute('rel', 'manifest');
        element.setAttribute('href', pathToManifest);
        const head = document.querySelector('head');
        if (head) {
            head.appendChild(element);
        }
    },
    isTuiBrand(brandSlug) {
        const tuiBrandSlugs = ['tui-blue'];
        return tuiBrandSlugs.includes(brandSlug);
    },
    isEmptyObject(obj) {
        return obj.constructor === Object && Object.keys(obj).length === 0;
    },
    getChromeVersion() {
        const raw = navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
        return raw ? parseInt(raw[2], 10) : null;
    },
    isChromeBrowser() {
        const getChromeVersion = () => {
            const raw = navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
            return raw ? parseInt(raw[2], 10) : null;
        };
        return getChromeVersion() !== null;
    },
    isSafariBrowser() {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        return isSafari;
    },
    isFirefoxBrowser() {
        return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    },
};
exports.Helpers = Helpers;
